import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Spin, Alert } from 'antd';
import { DocViewer } from './DocViewer';
import { Navigation } from './Navigation';
import { ApiKeyProvider } from './ApiKeyContext';
import { ApiKeyInput } from './ApiKeyInput';
import { CommonParamsProvider } from './CommonParamsContext';
import { CommonParamsInput } from './CommonParamsInput';
import './ApiDocsPage.css';

const { Sider, Content } = Layout;

interface DocFile {
  path: string;
  title: string;
  category: string;
}

const DOC_STRUCTURE: DocFile[] = [
  { path: 'README', title: 'Overview', category: 'main' },
  { path: 'authentication', title: 'Authentication', category: 'main' },
  { path: 'errors', title: 'Error Handling', category: 'main' },
  { path: 'CHANGELOG', title: 'Changelog', category: 'main' },
  { path: 'endpoints/seasons', title: 'Seasons', category: 'endpoints' },
  { path: 'endpoints/circuits', title: 'Circuits', category: 'endpoints' },
  { path: 'endpoints/events', title: 'Events', category: 'endpoints' },
  { path: 'endpoints/messages', title: 'Messages (Legacy)', category: 'endpoints' },
  { path: 'endpoints/infos', title: 'Infos (Legacy)', category: 'endpoints' },
  { path: 'endpoints/session-weather', title: 'Session Weather', category: 'endpoints' },
  { path: 'endpoints/station-observations', title: 'Station Observations', category: 'endpoints' },
  { path: 'endpoints/forecasts', title: 'Forecasts', category: 'endpoints' },
  { path: 'endpoints/forecast-fields', title: 'Forecast Fields', category: 'endpoints' },
  { path: 'endpoints/observation-fields', title: 'Observation Fields', category: 'endpoints' },
  { path: 'endpoints/radar', title: 'Radar', category: 'endpoints' },
  { path: 'endpoints/station-activity', title: 'Station Activity', category: 'endpoints' },
  { path: 'endpoints/alerts', title: 'Alerts', category: 'endpoints' },
  { path: 'examples/curl-examples', title: 'cURL Examples', category: 'examples' },
  { path: 'examples/javascript-examples', title: 'JavaScript Examples', category: 'examples' },
  { path: 'examples/python-examples', title: 'Python Examples', category: 'examples' },
  { path: 'schemas/data-models', title: 'Data Models', category: 'schemas' },
];

export const ApiDocsPage = () => {
  const { section, page } = useParams<{ section?: string; page?: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Determine the file path based on route params
  const getFilePath = (): string => {
    if (!section && !page) {
      return 'README';
    }
    if (section && page) {
      return `${section}/${page}`;
    }
    if (page) {
      return page;
    }
    return 'README';
  };

  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);
      setError(null);
      
      // Determine file path inline to avoid dependency issues
      let filePath: string;
      if (!section && !page) {
        filePath = 'README';
      } else if (section && page) {
        filePath = `${section}/${page}`;
      } else if (page) {
        filePath = page;
      } else {
        filePath = 'README';
      }
      
      const url = `/api-docs/${filePath}.md`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          if (response.status === 404) {
            setError(`Documentation file not found: ${filePath}.md`);
          } else {
            setError(`Failed to load documentation: ${response.statusText}`);
          }
          setContent('');
          return;
        }

        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError(`Error loading documentation: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setContent('');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [section, page]);

  // Handle hash navigation after content loads
  useEffect(() => {
    if (!loading && content && window.location.hash) {
      const hash = window.location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [loading, content]);

  const handleNavigation = (path: string) => {
    // Parse the path to determine section and page
    const parts = path.split('/');
    if (parts.length === 1) {
      // Single part: either README (go to base) or a root-level page
      if (path === 'README') {
        navigate('/api-docs');
      } else {
        navigate(`/api-docs/${path}`);
      }
    } else if (parts.length === 2) {
      // Two parts: section/page
      navigate(`/api-docs/${parts[0]}/${parts[1]}`);
    } else {
      // Fallback: use the full path
      navigate(`/api-docs/${path}`);
    }
  };

  const currentPath = getFilePath();

  return (
    <ApiKeyProvider>
      <CommonParamsProvider>
        <Layout className="api-docs-layout">
          <Sider
            width={280}
            className="api-docs-sider"
            theme="light"
            collapsible={false}
          >
            <Navigation
              docs={DOC_STRUCTURE}
              currentPath={currentPath}
              onNavigate={handleNavigation}
            />
          </Sider>
          <Content className="api-docs-content-wrapper">
            {loading ? (
              <div className="api-docs-loading">
                <Spin size="large" />
              </div>
            ) : error ? (
              <Alert
                title="Error Loading Documentation"
                description={error}
                type="error"
                showIcon
                style={{ margin: '24px' }}
              />
            ) : (
              <>
                <ApiKeyInput />
                <CommonParamsInput />
                <DocViewer content={content} />
              </>
            )}
          </Content>
        </Layout>
      </CommonParamsProvider>
    </ApiKeyProvider>
  );
};

